import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, X, Send, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Star Rating Display ────────────────────────────────────────────────────────
export function StarRating({ rating, size = "sm", showNumber = false }) {
  const sz = size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`${sz} ${i <= Math.round(rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"}`}
        />
      ))}
      {showNumber && (
        <span className="text-white/40 text-xs ml-1">{Number(rating).toFixed(1)}</span>
      )}
    </div>
  );
}

// ── Reputation Badge (compact) ─────────────────────────────────────────────────
export function ReputationBadge({ userId }) {
  const [rep, setRep] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${BACKEND_URL}/api/reputation/${userId}`)
      .then(r => setRep(r.data))
      .catch(() => {});
  }, [userId]);

  if (!rep || rep.total_reviews === 0) return null;

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-full hover:bg-[#D4AF37]/10 transition-all"
        data-testid={`reputation-badge-${userId}`}
      >
        <StarRating rating={rep.average_rating} />
        <span className="text-[#D4AF37] text-[11px] font-bold">{rep.average_rating}</span>
        <span className="text-white/30 text-[10px]">({rep.total_reviews})</span>
        {open ? <ChevronUp className="w-3 h-3 text-white/30" /> : <ChevronDown className="w-3 h-3 text-white/30" />}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-[#0A0A0A] border border-white/[0.08] rounded-xl p-4 z-50 shadow-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-1.5">
                <StarRating rating={rep.average_rating} showNumber />
              </div>
              <p className="text-white/30 text-xs mt-0.5">{rep.total_reviews} review{rep.total_reviews !== 1 ? "s" : ""}</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          {rep.badges?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {rep.badges.map(b => (
                <span key={b.id} className="text-[10px] px-2 py-0.5 rounded-full border font-semibold"
                  style={{ color: b.color, borderColor: `${b.color}40`, background: `${b.color}10` }}>
                  {b.label}
                </span>
              ))}
            </div>
          )}
          {rep.reviews?.slice(0, 3).map((r, i) => (
            <div key={i} className="border-t border-white/[0.04] pt-2 mt-2 first:border-0 first:mt-0 first:pt-0">
              <div className="flex items-center gap-1.5 mb-1">
                <StarRating rating={r.rating} size="sm" />
                <span className="text-white/30 text-[10px]">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.comment && <p className="text-white/50 text-xs">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Leave Review Modal ─────────────────────────────────────────────────────────
export function LeaveReviewModal({ targetUserId, targetName, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [category, setCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) { toast.error("Select a rating first"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reputation/review`, {
        reviewed_user_id: targetUserId,
        rating,
        comment: comment || null,
        category,
      }, { withCredentials: true });
      toast.success("Review submitted!");
      onSubmit?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-xl w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold">Rate {targetName}</h3>
            <p className="text-white/30 text-xs mt-0.5">Your honest review helps the community</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Star picker */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3, 4, 5].map(i => (
            <button key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(0)} onClick={() => setRating(i)}
              className="transition-transform hover:scale-110">
              <Star className={`w-8 h-8 transition-colors ${i <= (hovered || rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"}`} />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-white/40 text-xs -mt-2">
            {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
          </p>
        )}

        {/* Category */}
        <div className="space-y-1.5">
          <label className="text-white/50 text-xs">Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
            <option value="general">General</option>
            <option value="communication">Communication</option>
            <option value="reliability">Reliability</option>
            <option value="expertise">Expertise</option>
            <option value="deal_quality">Deal Quality</option>
          </select>
        </div>

        {/* Comment */}
        <div className="space-y-1.5">
          <label className="text-white/50 text-xs">Comment (optional)</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Share your experience working with this person..."
            rows={3}
            className="w-full bg-black border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none resize-none" />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-white/[0.08] text-white/50 rounded-lg text-sm hover:text-white transition-all">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || rating === 0}
            data-testid="submit-review-btn"
            className="flex-1 py-2.5 bg-[#D4AF37] hover:bg-[#D4AF37]/90 disabled:opacity-50 text-black font-bold rounded-lg text-sm transition-all flex items-center justify-center gap-2">
            {submitting ? <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> Submitting…</> : <><Send className="w-4 h-4" /> Submit Review</>}
          </button>
        </div>
      </div>
    </div>
  );
}
